import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Center,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { FiFile, FiStar, FiImage, FiFolder } from 'react-icons/fi'
import { FileItem, Folder } from '@/types'
import { useFavorites } from '@/contexts/FavoritesContext'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { containerVariants, listItemVariants } from '@/theme/animations'
import { MotionBox } from '@/components/Common/MotionBox'
import { useTrackNavigation } from '@/hooks/useTrackNavigation'

const MotionTr = motion(Tr as any)

export default function FavoritesPage() {
  const navigate = useNavigate()
  const { t, formatFileSize, formatDateTime } = useAppTranslation(['files', 'common'])
  const { favorites, isLoading } = useFavorites()
  useTrackNavigation('navigation.favorites')

  const getFileIcon = (item: FileItem | Folder, type: 'file' | 'folder') => {
    if (type === 'folder') return <FiFolder color="#3182CE" />
    const file = item as FileItem
    if (file.mime_type?.startsWith('image/')) return <FiImage color="#805ad5" />
    if (file.mime_type === 'application/pdf') return <FiFile color="#e53e3e" />
    return <FiFile />
  }

  const handleRowClick = (fav: { type: 'file' | 'folder'; item: FileItem | Folder }) => {
    if (fav.type === 'file') {
      navigate(`/preview/${fav.item.id}`)
    } else {
      navigate(`/#folder-${fav.item.id}`)
    }
  }

  if (isLoading) {
    return (
      <MotionBox initial="initial" animate="animate" variants={containerVariants} p={6} h="full">
        <Center h="full">
          <Spinner size="lg" color="brand.500" thickness="3px" />
        </Center>
      </MotionBox>
    )
  }

  return (
    <MotionBox
      initial="initial"
      animate="animate"
      variants={containerVariants}
      p={6}
      h="full"
      overflow="auto"
    >
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack spacing={3}>
          <Box color="yellow.500">
            <FiStar size="22px" />
          </Box>
          <Text fontSize="xl" fontWeight="bold">
            {t('favorites.title')}
          </Text>
          <Badge colorScheme="yellow" borderRadius="full" px={2}>
            {favorites.length}
          </Badge>
        </HStack>

        {/* Favorites Table */}
        {favorites.length === 0 ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Box color="gray.400">
                <FiStar size="48px" />
              </Box>
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium" color="gray.600">
                  {t('favorites.empty')}
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  {t('favorites.emptyDescription')}
                </Text>
              </VStack>
            </VStack>
          </Center>
        ) : (
          <MotionBox variants={listItemVariants}>
            <TableContainer
              bg="white"
              borderRadius="2xl"
              border="1px"
              borderColor="gray.200"
              boxShadow="sm"
              overflow="hidden"
            >
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th color="gray.700" fontWeight="semibold" fontSize="sm" letterSpacing="wide" borderColor="gray.200" py={3}>
                      {t('labels.name', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th color="gray.700" fontWeight="semibold" fontSize="sm" letterSpacing="wide" borderColor="gray.200" py={3}>
                      {t('labels.type', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th color="gray.700" fontWeight="semibold" fontSize="sm" letterSpacing="wide" borderColor="gray.200" py={3}>
                      {t('labels.location', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th color="gray.700" fontWeight="semibold" fontSize="sm" letterSpacing="wide" borderColor="gray.200" py={3}>
                      {t('favorites.favoritedDate')}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {favorites.map((fav, index) => {
                    const isFile = fav.type === 'file'
                    const item = fav.item
                    const name = isFile
                      ? (item as FileItem).display_name || (item as FileItem).original_name
                      : (item as Folder).name

                    return (
                      <MotionTr
                        key={fav.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.03 }}
                        cursor="pointer"
                        onClick={() => handleRowClick(fav)}
                        _hover={{ bg: 'blue.25' }}
                      >
                        <Td borderColor="gray.200" py={3}>
                          <HStack spacing={3}>
                            <Box color="gray.600">{getFileIcon(item, fav.type)}</Box>
                            <Text fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={1}>
                              {name}
                            </Text>
                          </HStack>
                        </Td>
                        <Td borderColor="gray.200" py={3}>
                          <Badge
                            colorScheme={isFile ? 'blue' : 'green'}
                            borderRadius="full"
                            px={2}
                            fontSize="xs"
                          >
                            {isFile ? t('labels.file', { ns: 'common' }) : t('labels.folder', { ns: 'common' })}
                          </Badge>
                        </Td>
                        <Td borderColor="gray.200" py={3}>
                          {isFile && (item as FileItem).folder ? (
                            <HStack spacing={1} color="gray.500">
                              <FiFolder size="13px" />
                              <Text fontSize="sm">{(item as FileItem).folder!.name}</Text>
                            </HStack>
                          ) : (
                            <Text fontSize="sm" color="gray.400">&mdash;</Text>
                          )}
                        </Td>
                        <Td borderColor="gray.200" py={3}>
                          <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                            {formatDateTime(new Date(fav.favorited_at))}
                          </Text>
                        </Td>
                      </MotionTr>
                    )
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </MotionBox>
        )}
      </VStack>
    </MotionBox>
  )
}
